/**
 * Cloud Connection Service
 * 
 * Handles connection testing, verification, and status checking for cloud providers
 */

import api from '../api'
import googleTokenRefreshService from '../googleTokenRefreshService'

class CloudConnectionService {
  constructor() {
    // Rate limiting for 401 errors
    this.last401Time = 0
    this.retryCount = 0
    this.maxRetries = 3
    this.retryDelay = 5000 // 5 seconds
    this.maxRetryDelay = 30000 // 30 seconds
    this.isRefreshingAuth = false
  }

  /**
   * Test Google Drive connection
   */
  async testGoogleDriveConnection() {
    // CRITICAL: Check if user is authenticated before testing connection
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 CloudConnectionService: User not authenticated, blocking connection test')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 CloudConnectionService: Onboarding detected, skipping connection test')
      throw new Error('Connection test not available during onboarding')
    }
    
    try {
      console.log('🔍 CloudConnectionService: Testing Google Drive connection...')
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/status`, {
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      })
      
      if (response.ok) {
        console.log('✅ CloudConnectionService: Google Drive connection test successful')
        return true
      } else {
        console.log('❌ CloudConnectionService: Google Drive connection test failed, status:', response.status)
        return false
      }
    } catch (error) {
      console.error('❌ CloudConnectionService: Google Drive connection test error:', error)
      return false
    }
  }

  /**
   * Verify cloud connection status with backend with rate limiting and auth token refresh
   */
  async verifyCloudConnectionWithBackend(authToken) {
    console.log('🚀 CloudConnectionService: verifyCloudConnectionWithBackend called with authToken:', !!authToken)
    
    // Skip verification during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 CloudConnectionService: Onboarding detected, skipping connection verification')
      return null
    }

    // Rate limiting for 401 errors
    const now = Date.now()
    if (this.last401Time > 0 && (now - this.last401Time) < this.retryDelay) {
      console.log('⏰ CloudConnectionService: Rate limited - too soon after 401 error')
      return null
    }

    // Check if we've exceeded max retries
    if (this.retryCount >= this.maxRetries) {
      console.log('❌ CloudConnectionService: Max retries exceeded, giving up')
      return null
    }
    
    try {
      console.log('🔍 CloudConnectionService: Verifying Google Drive connection with backend...')
      console.log('🔍 CloudConnectionService: Auth token length:', authToken?.length)
      console.log('🔍 CloudConnectionService: Auth token prefix:', authToken?.substring(0, 20) + '...')
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      console.log('🔍 CloudConnectionService: Using API base URL:', API_BASE_URL)
      
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/status`, {
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      })
      
      console.log('🔍 CloudConnectionService: Backend response status:', response.status)
      console.log('🔍 CloudConnectionService: Backend response headers:', Object.fromEntries(response.headers.entries()))

      if (response.ok) {
        // Reset retry count on success
        this.retryCount = 0
        this.last401Time = 0
        
        const data = await response.json()
        console.log('✅ CloudConnectionService: Backend verification response:', data)
        console.log('🔍 CloudConnectionService: Response details:', {
          connected: data.connected,
          hasAccessToken: !!data.accessToken,
          hasRefreshToken: !!data.refreshToken,
          hasUserInfo: !!data.userInfo,
          accessTokenLength: data.accessToken?.length,
          refreshTokenLength: data.refreshToken?.length,
          userInfoKeys: data.userInfo ? Object.keys(data.userInfo) : []
        })
        
        // Log the raw response for debugging
        console.log('🔍 CloudConnectionService: Raw backend response:', JSON.stringify(data, null, 2))
        
        return data
      } else if (response.status === 401) {
        // Handle 401 Unauthorized with rate limiting and auth token refresh
        console.log('❌ CloudConnectionService: Backend verification failed with 401 - auth token may be expired')
        
        // Update rate limiting
        this.last401Time = now
        this.retryCount++
        
        // Try to refresh auth token if we haven't already
        if (!this.isRefreshingAuth && this.retryCount <= 2) {
          console.log('🔄 CloudConnectionService: Attempting to refresh auth token...')
          this.isRefreshingAuth = true
          
          try {
            const refreshed = await this.attemptAuthTokenRefresh()
            if (refreshed) {
              console.log('✅ CloudConnectionService: Auth token refreshed, retrying verification')
              this.isRefreshingAuth = false
              
              // Wait for retry with new token after a short delay
              const newAuthToken = localStorage.getItem('authToken')
              console.log('🔄 CloudConnectionService: Retrying verification with refreshed auth token...')
              
              // Use a small delay to ensure the new token is properly set
              await new Promise(resolve => setTimeout(resolve, 1000))
              
              // Retry verification and wait for the result
              const retryResult = await this.verifyCloudConnectionWithBackend(newAuthToken)
              
              if (retryResult && retryResult.connected) {
                console.log('✅ CloudConnectionService: Cloud connection verified after auth token refresh')
                
                // Store the cloud tokens in localStorage
                if (retryResult.accessToken) {
                  localStorage.setItem('cloudAccessToken', retryResult.accessToken)
                  console.log('💾 CloudConnectionService: Stored cloudAccessToken in localStorage')
                }
                if (retryResult.refreshToken) {
                  localStorage.setItem('cloudRefreshToken', retryResult.refreshToken)
                  console.log('💾 CloudConnectionService: Stored cloudRefreshToken in localStorage')
                }
                if (retryResult.userInfo) {
                  localStorage.setItem('cloudUserInfo', JSON.stringify(retryResult.userInfo))
                  console.log('💾 CloudConnectionService: Stored cloudUserInfo in localStorage')
                }
                localStorage.setItem('cloudProvider', 'google_drive')
                
                // Dispatch event to notify other services that cloud tokens are available
                window.dispatchEvent(new CustomEvent('cloudTokensRefreshed', {
                  detail: {
                    accessToken: retryResult.accessToken,
                    refreshToken: retryResult.refreshToken,
                    userInfo: retryResult.userInfo,
                    connected: retryResult.connected
                  }
                }))
                console.log('📡 CloudConnectionService: Dispatched cloudTokensRefreshed event')
                
                return retryResult
              } else {
                console.log('❌ CloudConnectionService: Cloud connection verification failed even after auth token refresh')
                return null
              }
            }
          } catch (error) {
            console.error('❌ CloudConnectionService: Failed to refresh auth token:', error)
          } finally {
            this.isRefreshingAuth = false
          }
        }
        
        // If we can't refresh or have exceeded retries, clear auth and trigger re-login
        if (this.retryCount >= this.maxRetries) {
          console.log('❌ CloudConnectionService: Max retries reached, clearing auth and triggering re-login')
          this.clearAuthAndTriggerReLogin()
        }
        
        return null
      } else {
        console.log('❌ CloudConnectionService: Backend verification failed, status:', response.status)
        return null
      }
    } catch (error) {
      console.error('❌ CloudConnectionService: Error verifying backend connection:', error)
      return null
    }
  }

  /**
   * Attempt to refresh tokens using existing refresh token
   */
  async attemptTokenRefresh() {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        console.log('❌ CloudConnectionService: No refresh token available for token refresh')
        return null
      }

      console.log('🔄 CloudConnectionService: Refreshing tokens using refresh token...')
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refreshToken: refreshToken
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.token) {
          // Update auth token in localStorage
          localStorage.setItem('authToken', data.token)
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken)
          }
          console.log('✅ CloudConnectionService: Tokens refreshed successfully')
          return data.token
        }
      }
      
      console.log('❌ CloudConnectionService: Token refresh failed:', response.status)
      return null
    } catch (error) {
      console.error('❌ CloudConnectionService: Error refreshing tokens:', error)
      return null
    }
  }

  /**
   * Check if user is authenticated
   */
  isUserAuthenticated() {
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    return !!(authToken && user)
  }

  /**
   * Check if currently in onboarding
   */
  isInOnboarding() {
    return window.location.pathname.includes('/onboarding')
  }

  /**
   * Attempt to refresh the authentication token
   */
  async attemptAuthTokenRefresh() {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        console.log('❌ CloudConnectionService: No refresh token available for auth token refresh')
        return false
      }

      console.log('🔄 CloudConnectionService: Refreshing auth token using refresh token...')
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refreshToken: refreshToken
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.token) {
          // Update auth token in localStorage
          localStorage.setItem('authToken', data.token)
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken)
          }
          console.log('✅ CloudConnectionService: Auth token refreshed successfully')
          return true
        }
      }
      
      console.log('❌ CloudConnectionService: Auth token refresh failed:', response.status)
      return false
    } catch (error) {
      console.error('❌ CloudConnectionService: Error refreshing auth token:', error)
      return false
    }
  }

  /**
   * Clear authentication and trigger re-login
   */
  clearAuthAndTriggerReLogin() {
    console.log('🧹 CloudConnectionService: Clearing authentication and triggering re-login')
    
    // Clear all auth-related localStorage
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('cloudAccessToken')
    localStorage.removeItem('cloudRefreshToken')
    localStorage.removeItem('cloudUserInfo')
    
    // Dispatch event to trigger re-login
    window.dispatchEvent(new CustomEvent('authTokenExpired', {
      detail: {
        reason: 'AUTH_TOKEN_EXPIRED',
        message: 'Your session has expired. Please log in again.',
        requiresReauth: true
      }
    }))
    
    // Reset retry state
    this.retryCount = 0
    this.last401Time = 0
  }

  /**
   * Reset retry state (useful for manual recovery)
   */
  resetRetryState() {
    this.retryCount = 0
    this.last401Time = 0
    this.isRefreshingAuth = false
    console.log('🔄 CloudConnectionService: Retry state reset')
  }
}

export default new CloudConnectionService()